import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, UserPlus, X } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { formatIDR, todayStr, shiftDateStr, SOURCES } from '../lib/format';
import { BarangRow } from './transaction/BarangRow';
import { PaymentEntries } from './transaction/PaymentEntries';
import { HapusCari } from './HapusCari';

const inputCls =
  'w-full rounded-xl border border-input bg-ivory/50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-brown/30 transition-shadow';
const labelCls = 'text-xs font-semibold uppercase tracking-wider text-mocha';

const JAMINAN_OPTIONS = [
  { value: 'titip_ktp', label: 'Titip KTP' },
  { value: 'kirim_wa', label: 'Kirim WA' },
  { value: 'tidak_ada', label: 'Tanpa Jaminan' },
];

const EVENT_OPTIONS = ['Pernikahan', 'Akad', 'Lamaran', 'Wisuda', 'Pesta/Resepsi', 'Foto/Pre-wedding', 'Pengajian'];

export default function TransactionForm({ initial = null, txId = null }) {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [customerQuery, setCustomerQuery] = useState(initial?.customer_name || '');
  const [selectedCustomer, setSelectedCustomer] = useState(
    initial ? { id: initial.customer_id, name: initial.customer_name, whatsapp: initial.customer_whatsapp } : null
  );
  const [whatsapp, setWhatsapp] = useState(initial?.customer_whatsapp || '');
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [source, setSource] = useState(initial?.source || 'Walk-in / Spanduk');
  const [eventChoice, setEventChoice] = useState(
    !initial?.event ? '' : EVENT_OPTIONS.includes(initial.event) ? initial.event : 'Lainnya'
  );
  const [eventCustom, setEventCustom] = useState(
    initial?.event && !EVENT_OPTIONS.includes(initial.event) ? initial.event : ''
  );
  const [ktpType, setKtpType] = useState(initial?.ktp_type || (initial?.ktp ? 'titip_ktp' : 'tidak_ada'));
  const [ktpName, setKtpName] = useState(initial?.ktp_name || initial?.ktp || '');

  const [useDate, setUseDate] = useState(initial?.use_date || todayStr());
  const [customDates, setCustomDates] = useState(initial?.custom_dates || false);
  const [pickup, setPickup] = useState(initial?.pickup_date || shiftDateStr(initial?.use_date || todayStr(), -1));
  const [ret, setRet] = useState(initial?.return_date || shiftDateStr(initial?.use_date || todayStr(), 1));
  const [ready, setReady] = useState(initial?.ready_date || shiftDateStr(initial?.use_date || todayStr(), 1));

  const effPickup = customDates ? pickup : shiftDateStr(useDate, -1);
  const effReturn = customDates ? ret : shiftDateStr(useDate, 1);
  const effReady = customDates ? ready : effReturn;

  const [availability, setAvailability] = useState([]);
  const [rows, setRows] = useState(
    initial?.rows?.map((r) => ({
      product_id: r.product_id,
      sizes: { ...r.sizes },
      price_per_pcs: r.price_per_pcs,
      bawahan_product_id: r.bawahan_product_id || null,
      bawahan_sizes: r.bawahan_custom ? { ...(r.bawahan_sizes || {}) } : {},
      bawahan_custom: !!r.bawahan_custom,
      // Order lama (belum ada aksesoris) dibuka dengan toggle mati: tidak diubah.
      aksesoris: (r.aksesoris || []).map((a) => a.nama),
    })) || [{ product_id: null, sizes: {}, price_per_pcs: null, bawahan_product_id: null, bawahan_sizes: {}, bawahan_custom: false, aksesoris: [] }]
  );

  const [payEntries, setPayEntries] = useState([]);
  const [notes, setNotes] = useState(initial?.notes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/customers').then((r) => setCustomers(r.data));
  }, []);

  useEffect(() => {
    if (!effPickup || !effReady) return;
    api
      .get(`/products-availability?start=${effPickup}&end=${effReady}${txId ? `&exclude_tx=${txId}` : ''}`)
      .then((r) => setAvailability(r.data))
      .catch(() => toast.error('Gagal memuat stok'));
  }, [effPickup, effReady, txId]);

  const filteredCustomers = useMemo(
    () => customers.filter((c) => c.name.toLowerCase().includes(customerQuery.toLowerCase())),
    [customers, customerQuery]
  );

  const total = rows.reduce((s, r) => {
    const q = Object.values(r.sizes).reduce((a, b) => a + b, 0);
    return s + q * (r.price_per_pcs || 0);
  }, 0);
  const totalPcs = rows.reduce((s, r) => s + Object.values(r.sizes).reduce((a, b) => a + b, 0), 0);
  const paidTotal = payEntries.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const remaining = Math.max(0, total - (txId ? initial?.paid || 0 : paidTotal));

  const addRow = () =>
    setRows([...rows, { product_id: null, sizes: {}, price_per_pcs: null, bawahan_product_id: null, bawahan_sizes: {}, bawahan_custom: false, aksesoris: [] }]);

  const submit = async () => {
    if (!selectedCustomer && !customerQuery.trim()) return toast.error('Pilih atau ketik nama customer');
    if (rows.length === 0 || rows.some((r) => !r.product_id)) return toast.error('Lengkapi pilihan barang');
    if (rows.some((r) => Object.values(r.sizes).reduce((a, b) => a + b, 0) === 0))
      return toast.error('Isi jumlah ukuran untuk setiap barang');
    setSaving(true);
    try {
      const payload = {
        customer_id: selectedCustomer?.id || null,
        new_customer_name: selectedCustomer ? '' : customerQuery.trim(),
        new_customer_whatsapp: whatsapp,
        use_date: useDate,
        pickup_date: effPickup,
        return_date: effReturn,
        ready_date: effReady,
        custom_dates: customDates,
        event: eventChoice === 'Lainnya' ? eventCustom.trim() : eventChoice,
        ktp_type: ktpType,
        ktp_name: ktpType === 'titip_ktp' ? ktpName : '',
        rows: rows.map((r) => ({
          product_id: r.product_id,
          sizes: r.sizes,
          price_per_pcs: r.price_per_pcs,
          bawahan_product_id: r.bawahan_product_id || null,
          bawahan_sizes: r.bawahan_custom ? r.bawahan_sizes : {},
          bawahan_custom: !!r.bawahan_custom,
          aksesoris: r.aksesoris || [],
        })),
        source,
        notes,
      };
      if (!txId) {
        payload.payments = payEntries
          .filter((e) => Number(e.amount) > 0)
          .map((e) => ({ amount: Number(e.amount), method: e.method, date: e.date, kind: e.kind }));
      }
      const { data } = txId ? await api.put(`/transactions/${txId}`, payload) : await api.post('/transactions', payload);
      toast.success(`Order ${data.transaction_number} tersimpan`);
      navigate(`/transaksi/${data.id}`);
    } catch (err) {
      toast.error(apiError(err, 'Gagal menyimpan order'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4 items-start" data-testid="transaction-form">
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white rounded-2xl border border-border shadow-soft p-5">
          <h2 className="font-display text-lg text-brown-dark mb-4">Customer</h2>
          <div className="relative">
            <input
              data-testid="customer-autocomplete-input"
              value={customerQuery}
              onChange={(e) => {
                setCustomerQuery(e.target.value);
                // Bug 31 Okt 2026: nama diubah setelah memilih customer -> nomor
                // customer sebelumnya tertinggal dan ikut tersimpan ke customer baru.
                if (selectedCustomer && whatsapp === (selectedCustomer.whatsapp || '')) setWhatsapp('');
                setSelectedCustomer(null);
                setShowCustomerList(true);
              }}
              onFocus={() => setShowCustomerList(true)}
              onBlur={() => setTimeout(() => setShowCustomerList(false), 200)}
              placeholder="Ketik nama customer…"
              className={`${inputCls} pr-9`}
            />
            <HapusCari
              show={!!customerQuery}
              onClear={() => { setCustomerQuery(''); setSelectedCustomer(null); setWhatsapp(''); setShowCustomerList(false); }}
              testid="customer-search-clear"
            />
            {showCustomerList && customerQuery && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-border rounded-xl shadow-lift max-h-48 overflow-y-auto" data-testid="customer-suggestions">
                {filteredCustomers.slice(0, 10).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`customer-option-${c.id}`}
                    onMouseDown={() => {
                      setSelectedCustomer(c);
                      setCustomerQuery(c.name);
                      setWhatsapp(c.whatsapp || '');
                      setShowCustomerList(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-ivory flex justify-between"
                  >
                    <span className="font-semibold">{c.name}</span>
                    <span className="text-mocha text-xs">{c.whatsapp}</span>
                  </button>
                ))}
                {filteredCustomers.length === 0 && (
                  <div className="px-4 py-3 text-xs text-mocha flex items-center gap-2">
                    <UserPlus size={14} /> Customer baru akan dibuat: <b>{customerQuery}</b>
                  </div>
                )}
              </div>
            )}
          </div>
          {selectedCustomer && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-brown/10 text-brown text-xs font-bold px-3 py-1.5" data-testid="selected-customer-chip">
              <Check size={13} /> {selectedCustomer.name}
              <button type="button" data-testid="clear-customer-button" onClick={() => {
                if (whatsapp === (selectedCustomer.whatsapp || '')) setWhatsapp('');
                setSelectedCustomer(null); setCustomerQuery('');
              }}>
                <X size={13} />
              </button>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <div>
              <label className={labelCls}>WhatsApp</label>
              <input data-testid="customer-whatsapp-input" type="tel" inputMode="numeric" autoComplete="tel" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="08xx-xxxx-xxxx" className={`${inputCls} mt-1.5`} />
            </div>
            <div>
              <label className={labelCls}>Acara</label>
              <select data-testid="event-select" value={eventChoice} onChange={(e) => setEventChoice(e.target.value)} className={`${inputCls} mt-1.5`}>
                <option value="">Pilih acara…</option>
                {EVENT_OPTIONS.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
                <option value="Lainnya">Lainnya (ketik sendiri)</option>
              </select>
              {eventChoice === 'Lainnya' && (
                <input data-testid="event-custom-input" value={eventCustom} onChange={(e) => setEventCustom(e.target.value)} placeholder="Ketik nama acara…" className={`${inputCls} mt-2`} />
              )}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <div>
              <label className={labelCls}>Jaminan</label>
              <select data-testid="jaminan-select" value={ktpType} onChange={(e) => setKtpType(e.target.value)} className={`${inputCls} mt-1.5`}>
                {JAMINAN_OPTIONS.map((j) => <option key={j.value} value={j.value}>{j.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Sumber Order</label>
              <select data-testid="source-select" value={source} onChange={(e) => setSource(e.target.value)} className={`${inputCls} mt-1.5`}>
                {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          {ktpType === 'titip_ktp' && (
            <div className="mt-3">
              <label className={labelCls}>Nama di KTP</label>
              <input data-testid="ktp-name-input" value={ktpName} onChange={(e) => setKtpName(e.target.value)} placeholder="Nama sesuai KTP yang dititipkan" className={`${inputCls} mt-1.5`} />
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-border shadow-soft p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h2 className="font-display text-lg text-brown-dark">Tanggal</h2>
            <button
              type="button"
              data-testid="custom-dates-toggle"
              onClick={() => {
                const next = !customDates;
                setCustomDates(next);
                if (next) {
                  setPickup(shiftDateStr(useDate, -1));
                  setRet(shiftDateStr(useDate, 1));
                  setReady(shiftDateStr(useDate, 1));
                }
              }}
              className={`rounded-full px-4 py-1.5 text-[11px] font-bold transition-colors ${
                customDates ? 'bg-caramel text-white' : 'bg-ivory border border-border text-mocha'
              }`}
            >
              {customDates ? 'Custom aktif' : 'Custom tanggal ambil/kembali'}
            </button>
          </div>
          <div className={customDates ? 'grid sm:grid-cols-4 gap-3' : 'grid sm:grid-cols-3 gap-3'}>
            <div>
              <label className={labelCls}>Tgl Pakai</label>
              <input data-testid="use-date-input" type="date" value={useDate} onChange={(e) => setUseDate(e.target.value)} className={`${inputCls} mt-1.5`} />
            </div>
            <div>
              <label className={labelCls}>Ambil (H-1)</label>
              {customDates ? (
                <input data-testid="pickup-date-input" type="date" value={pickup} max={useDate} onChange={(e) => setPickup(e.target.value)} className={`${inputCls} mt-1.5`} />
              ) : (
                <p data-testid="pickup-date-auto" className="mt-1.5 rounded-xl bg-brown/5 border border-brown/15 px-4 py-3 text-sm font-bold text-brown">{effPickup}</p>
              )}
            </div>
            <div>
              <label className={labelCls}>Kembali (H+1)</label>
              {customDates ? (
                <input data-testid="return-date-input" type="date" value={ret} min={useDate} onChange={(e) => setRet(e.target.value)} className={`${inputCls} mt-1.5`} />
              ) : (
                <p data-testid="return-date-auto" className="mt-1.5 rounded-xl bg-brown/5 border border-brown/15 px-4 py-3 text-sm font-bold text-brown">{effReturn}</p>
              )}
            </div>
            {customDates && (
              <div>
                <label className={labelCls}>Ready Lagi</label>
                <input data-testid="ready-date-input" type="date" value={ready} min={ret} onChange={(e) => setReady(e.target.value)} className={`${inputCls} mt-1.5`} />
              </div>
            )}
          </div>
          <p className="text-[11px] text-mocha mt-3">
            Otomatis: barang diblokir dari tanggal ambil (H-1) sampai tanggal kembali (H+1). Aktifkan custom untuk mengubahnya.
          </p>
        </div>

        {rows.map((row, i) => (
          <BarangRow
            key={i}
            index={i}
            row={row}
            availability={availability}
            onChange={(r) => setRows(rows.map((x, j) => (j === i ? r : x)))}
            onRemove={() => setRows(rows.filter((_, j) => j !== i))}
          />
        ))}
        <button
          type="button"
          data-testid="add-barang-button"
          onClick={addRow}
          className="w-full rounded-2xl border-2 border-dashed border-brown/25 bg-white/60 py-4 text-sm font-semibold text-brown hover:bg-white hover:border-brown/40 transition-colors"
        >
          + Tambah Barang
        </button>
      </div>

      <div className="bg-white rounded-2xl border-2 border-gold/40 shadow-soft overflow-hidden lg:sticky lg:top-6" data-testid="transaction-summary">
        <div className="relative bg-choco px-5 py-3">
          <p className="font-display text-gold text-lg tracking-wide">Ringkasan</p>
          <div className="absolute bottom-0 inset-x-0 h-0.5 songket-strip opacity-70" />
        </div>
        <div className="p-5 space-y-4">
          <div className="space-y-1.5">
            {rows.filter((r) => r.product_id).map((r, i) => {
              const p = availability.find((x) => x.id === r.product_id);
              const q = Object.values(r.sizes).reduce((a, b) => a + b, 0);
              return (
                <div key={i} className="flex justify-between text-sm gap-2">
                  <span className="truncate"><b className="text-brown">{p?.code}</b> {p?.name}</span>
                  <span className="whitespace-nowrap">{q} × {formatIDR(r.price_per_pcs || 0)}</span>
                </div>
              );
            })}
            {rows.filter((r) => r.product_id).length === 0 && <p className="text-xs text-mocha">Belum ada barang dipilih.</p>}
          </div>
          <div className="border-t border-dashed border-gold/40 pt-3 space-y-1">
            <div className="flex justify-between text-sm text-mocha"><span>Total potong</span><span data-testid="summary-pcs">{totalPcs} pcs</span></div>
            <div className="flex justify-between font-display text-xl text-ink"><span>Total</span><span data-testid="summary-total">{formatIDR(total)}</span></div>
          </div>

          {!txId ? (
            <div>
              <label className={labelCls}>Pembayaran (DP / Pelunasan)</label>
              <div className="mt-2">
                <PaymentEntries entries={payEntries} setEntries={setPayEntries} total={total} />
              </div>
            </div>
          ) : (
            <p className="text-xs text-mocha bg-ivory rounded-xl px-3 py-2.5">
              Pembayaran yang sudah tercatat tidak berubah saat edit. Sisa menyesuaikan total baru otomatis.
            </p>
          )}
          <div className="flex justify-between text-sm"><span className="text-mocha">Dibayar</span><span data-testid="summary-paid" className="font-bold">{formatIDR(txId ? initial?.paid || 0 : paidTotal)}</span></div>
          <div className="flex justify-between font-bold text-brown"><span>Sisa Pembayaran</span><span data-testid="summary-remaining">{formatIDR(remaining)}</span></div>

          <textarea
            data-testid="notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Catatan (opsional)…"
            rows={2}
            className={inputCls}
          />

          <button
            type="button"
            data-testid="save-transaction-button"
            onClick={submit}
            disabled={saving}
            className="w-full rounded-xl bg-brown text-white py-3.5 font-semibold text-sm hover:bg-brown-med active:scale-[0.98] transition-all duration-150 disabled:opacity-60"
          >
            {saving ? 'Menyimpan…' : txId ? 'Simpan Perubahan' : 'Simpan Order'}
          </button>
        </div>
      </div>
    </div>
  );
}
